import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { ToastService } from '../../../../core/services/toast.service';
import {
  BreadcrumbItem,
  BreadcrumbsComponent,
} from '../../../../shared/components/breadcrumbs/breadcrumbs.component';
import { UserRole } from '../../../../core/auth/models/auth.models';
import { PartnerOption, UserApiModel } from '../../models/user.models';
import { UsersService } from '../../services/users.service';

@Component({
  selector: 'app-user-profile',
  imports: [CommonModule, ReactiveFormsModule, BreadcrumbsComponent],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly usersService = inject(UsersService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);

  readonly userId = Number(this.route.snapshot.paramMap.get('id'));

  readonly loading = signal(true);
  readonly savingProfile = signal(false);
  readonly updatingStatus = signal(false);
  readonly resettingPassword = signal(false);
  readonly loadingPartners = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly pendingStatusChange = signal<boolean | null>(null);

  readonly user = signal<UserApiModel | null>(null);
  readonly partners = signal<PartnerOption[]>([]);

  readonly isOwnAccount = computed(() => this.authService.user()?.id === this.userId);
  readonly showAccountStatus = computed(() => !this.isOwnAccount());
  readonly showProfileAuditDetails = computed(() => !this.isOwnAccount());

  readonly passwordSectionTitle = computed(() =>
    this.isOwnAccount() ? 'Change Password' : 'Reset Password',
  );
  readonly passwordSectionDescription = computed(() =>
    this.isOwnAccount() ? 'Change your password.' : 'Set a new password for this user.',
  );
  readonly passwordSubmitLabel = computed(() =>
    this.isOwnAccount() ? 'Change Password' : 'Reset Password',
  );
  readonly passwordSubmittingLabel = computed(() =>
    this.isOwnAccount() ? 'Changing...' : 'Resetting...',
  );
  readonly pendingStatusActionLabel = computed(() =>
    this.pendingStatusChange() ? 'Enable User' : 'Disable User',
  );
  readonly pendingStatusDescription = computed(() =>
    this.pendingStatusChange()
      ? 'This user will be able to sign in and use the application.'
      : 'This user will no longer be able to sign in or use the application.',
  );

  readonly displayName = computed(() => {
    const user = this.user();
    if (!user) {
      return 'User';
    }
    return user.fullName || 'User';
  });

  readonly breadcrumbItems = computed<BreadcrumbItem[]>(() => [
    { label: 'Home', route: '/home' },
    { label: 'Users', route: '/users' },
    { label: this.displayName() },
  ]);

  readonly profileForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
    fullName: ['', [Validators.required, Validators.maxLength(255)]],
    role: ['partner' as UserRole, [Validators.required]],
    partnerOrganizationId: [''],
  });

  readonly resetPasswordForm = this.fb.nonNullable.group({
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: ['', [Validators.required]],
  });

  constructor() {
    this.resetPasswordForm.addValidators((group) => {
      const password = group.get('password')?.value ?? '';
      const confirmPassword = group.get('confirmPassword')?.value ?? '';
      return password === confirmPassword ? null : { passwordMismatch: true };
    });

    this.loadUser();
    this.loadPartners();

    this.profileForm.controls.role.valueChanges.subscribe((role) => {
      if (role !== 'partner') {
        this.profileForm.controls.partnerOrganizationId.setValue('', { emitEvent: false });
      }
    });
  }

  goBackToUsers(): void {
    void this.router.navigate(['/users']);
  }

  saveProfile(): void {
    if (!this.userId || Number.isNaN(this.userId)) {
      this.toastService.danger('Save Failed', 'Invalid user reference.');
      return;
    }

    if (this.profileForm.invalid || this.savingProfile()) {
      this.profileForm.markAllAsTouched();
      return;
    }

    const raw = this.profileForm.getRawValue();
    const role = raw.role as UserRole;
    const partnerOrganizationId =
      role === 'partner'
        ? this.toPartnerOrganizationIdOrNull(raw.partnerOrganizationId)
        : null;

    this.savingProfile.set(true);

    this.usersService
      .updateUserProfile(this.userId, {
        email: raw.email.trim().toLowerCase(),
        fullName: raw.fullName.trim(),
        role,
        partnerOrganizationId,
      })
      .pipe(finalize(() => this.savingProfile.set(false)))
      .subscribe({
        next: ({ user }) => {
          this.user.set(user);
          this.patchProfileForm(user);
          this.toastService.success('Profile Updated', 'User profile updated successfully.');
        },
        error: (error) => {
          this.toastService.danger(
            'Update Failed',
            error?.error?.message ?? 'Unable to update this user right now.',
          );
        },
      });
  }

  toggleUserStatus(): void {
    const current = this.user();
    if (current) {
      this.setUserStatus(!current.isActive);
    }
  }

  setUserStatus(isActive: boolean): void {
    const current = this.user();
    if (!current || this.updatingStatus()) {
      return;
    }

    if (current.isActive === isActive) {
      return;
    }

    if (this.isOwnAccount() && !isActive) {
      this.toastService.warning('Not Allowed', 'You cannot disable your own account.');
      return;
    }

    this.pendingStatusChange.set(isActive);
  }

  cancelStatusChange(): void {
    if (this.updatingStatus()) {
      return;
    }

    this.pendingStatusChange.set(null);
  }

  confirmStatusChange(): void {
    const isActive = this.pendingStatusChange();
    if (isActive === null || this.updatingStatus()) {
      return;
    }

    this.pendingStatusChange.set(null);
    this.applyUserStatus(isActive);
  }

  private applyUserStatus(isActive: boolean): void {
    const current = this.user();
    if (!current || this.updatingStatus()) {
      return;
    }

    if (current.isActive === isActive) {
      return;
    }

    if (this.isOwnAccount() && !isActive) {
      this.toastService.warning('Not Allowed', 'You cannot disable your own account.');
      return;
    }

    this.updatingStatus.set(true);
    this.usersService
      .updateUserStatus(current.id, { isActive })
      .pipe(finalize(() => this.updatingStatus.set(false)))
      .subscribe({
        next: ({ user }) => {
          this.user.set(user);
          this.patchProfileForm(user);
          this.toastService.success(
            'Status Updated',
            user.isActive ? 'User has been enabled.' : 'User has been disabled.',
          );
        },
        error: (error) => {
          this.toastService.danger(
            'Status Update Failed',
            error?.error?.message ?? 'Unable to update user status right now.',
          );
        },
      });
  }

  resetPassword(): void {
    if (!this.userId || Number.isNaN(this.userId)) {
      this.toastService.danger('Reset Failed', 'Invalid user reference.');
      return;
    }

    if (this.resetPasswordForm.invalid || this.resettingPassword()) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }

    const { password } = this.resetPasswordForm.getRawValue();

    this.resettingPassword.set(true);

    this.usersService
      .resetUserPassword(this.userId, { password })
      .pipe(finalize(() => this.resettingPassword.set(false)))
      .subscribe({
        next: () => {
          this.resetPasswordForm.reset();
          this.toastService.success('Password Reset', 'User password reset successfully.');
        },
        error: (error) => {
          this.toastService.danger(
            'Reset Failed',
            error?.error?.message ?? 'Unable to reset password right now.',
          );
        },
      });
  }

  private loadUser(): void {
    if (!this.userId || Number.isNaN(this.userId)) {
      this.errorMessage.set('Invalid user reference.');
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);

    this.usersService
      .getUserById(this.userId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: ({ user }) => {
          this.user.set(user);
          this.patchProfileForm(user);
        },
        error: (error) => {
          this.errorMessage.set(error?.error?.message ?? 'Unable to load user profile right now.');
        },
      });
  }

  private loadPartners(): void {
    this.loadingPartners.set(true);

    this.usersService
      .listPartners()
      .pipe(finalize(() => this.loadingPartners.set(false)))
      .subscribe({
        next: ({ data }) => {
          this.partners.set(data);
        },
        error: () => {
          this.partners.set([]);
        },
      });
  }

  private patchProfileForm(user: UserApiModel): void {
    this.profileForm.patchValue({
      email: user.email,
      fullName: user.fullName,
      role: user.role,
      partnerOrganizationId:
        user.partnerOrganizationId == null ? '' : String(user.partnerOrganizationId),
    });
  }

  private toPartnerOrganizationIdOrNull(value: string): number | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) {
      return null;
    }

    return parsed;
  }
}
